import React from 'react';

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { type InvoiceMonth, formatMonthLabel } from './monthRange';

interface Props {
  open: boolean;
  partnerName: string;
  month: InvoiceMonth;
  isRefreshing: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation for recomputing a period's costs.
 *
 * Deliberately its own dialog rather than the shared `useConfirmDialog`, which renders a single
 * line of plain text: this action overwrites stored costs irreversibly, so it has to explain when
 * it is the right thing to do and be explicit that any previously saved invoice for the period is
 * replaced and cannot be recovered.
 */
export default function ForceRefreshDialog({ open, partnerName, month, isRefreshing, onConfirm, onCancel }: Props) {
  return (
    <Dialog open={open} onClose={isRefreshing ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Force refresh invoice costs?</DialogTitle>

      <DialogContent>
        <Typography variant="body2" gutterBottom>
          This recalculates the cost of every transaction for <strong>{partnerName || 'this partner'}</strong> in{' '}
          <strong>{formatMonthLabel(month)}</strong> and overwrites the stored figures.
        </Typography>

        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            When you need this
          </Typography>
          <Typography variant="body2" component="div">
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              <li>A transaction was inserted into a past period that had already been costed.</li>
              <li>An existing transaction in a past period was updated.</li>
            </Box>
          </Typography>
          <Typography variant="body2" mt={1}>
            Either changes what the period should cost, and on a tiered rate card it also shifts every later transaction
            into a different band — so the whole period has to be priced again.
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            You do not need this the first time you open a period, which is priced and stored as you view it, nor after
            editing a rate card, which invalidates the stored costs on its own.
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>This cannot be undone</AlertTitle>
          If an invoice was already saved for this period, refreshing replaces the data behind it. The new invoice may
          differ from the one that was saved, and the previous version will no longer be available anywhere.
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={isRefreshing}>
          Cancel
        </Button>
        <Button variant="contained" color="warning" onClick={onConfirm} disabled={isRefreshing}>
          {isRefreshing ? 'Recalculating…' : 'Recalculate costs'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
